import { colorToCss } from "../../color/color"
import { NumericValueColorProvider } from "../../color/provider"
import { LayerTypeId } from "../../generated/enums"
import { ICropYieldScale } from "../../models/yield"
import { LocaleString } from "../../types"
import { createRange } from "../../utils/array"
import { isFunction } from "../../utils/utils"
import { IDatalayerKey, IDatalayerMapInstance, IDataLayerType, IDatalayerViewerFactory } from "../types"
import { NumericValuesViewer } from "./numeric"

/// <reference path="numeric.ts" />

export class YieldMapViewer extends NumericValuesViewer {

    private customScale: ICropYieldScale

    private intervals: {
        label: string
        min: number
        max: number
        color: number[]
        cssColor: string
    }[]

    public static createFactory(): IDatalayerViewerFactory {

        return {
            typeId: LayerTypeId.Yield,
            name: "Yield" as LocaleString,
            createViewer: layer => new YieldMapViewer(layer)
        }

    }

    public static createYieldAverageFactory(): IDatalayerViewerFactory {

        return {
            typeId: LayerTypeId.YieldAverage,
            name: "Yield average",
            createViewer: layer => new YieldMapViewer(layer)
        }

    }

    protected setupNumericLayer(type: IDataLayerType, colorProvider: NumericValueColorProvider) {

        this.setupCustomScale()

        super.setupNumericLayer(type, colorProvider)

    }

    protected setupMinMaxZoneValues() {


        const scale = this.customScale
        if (scale) {
            this._minZoneValue = scale.MinYield;
            this._maxZoneValue = scale.MaxYield;
            return
        }

        const crop = this._layer.crop;
        if (crop && crop.YieldMapMin && crop.YieldMapMax) {
            this._minZoneValue = crop.YieldMapMin;
            this._maxZoneValue = crop.YieldMapMax;
            return
        }

        super.setupMinMaxZoneValues();

    }

    protected getLayersForValueRange(): IDatalayerMapInstance[] {

        let layers = [this._layer];

        let cropId = this._layer.cropId;

        if (cropId && isFunction(this._layer.context.getAllLayers)) {

            layers = layers.concat(
                this._layer.context.getAllLayers()
                    .filter(l => (l.cropId == cropId) && (l != this._layer))
            );

        }

        return layers;

    }

    public getValueFillColor(value: number, alpha: number = 1) {

        let color: number[]

        const intervals = this.intervals

        if (intervals) {

            const match = intervals.find(i => (i.min <= value) && (i.max > value))

            if (match) {
                color = match.color.slice()
            }

        }

        if (!color) {
            color = super.getValueFillColor(value, alpha);
        }

        if (color) {
            color[3] = alpha
        }
        else {
            color = [255, 255, 255, 0.5]
        }

        return color

    }

    getKey(): IDatalayerKey {

        const { crop } = this._layer
        const cropId = crop ? crop.CropId : 0

        const id = `yield-crop-${cropId}`
        const name = "Yield" + (crop ? " - " + crop.Name : "")

        const intervals = this.intervals

        if (intervals) {

            const key = this.intervals.map(i => ({
                value: i.min,
                text: i.label,
                color: i.cssColor
            }))

            return {
                id,
                name,
                layerKey: key,
                typeKey: key,
                numeric: true,
                fixed: true,
                unit: "t/ha"
            }
        }

        const key = super.getKey();

        return {
            ...key,
            id,
            name,
            unit: "t/ha"
        }

    }

    private setupCustomScale() {

        this.intervals = undefined

        const scale = this.customScale = this.getCustomCropScale()
        if (!this.customScale) return

        const intervals = scale.Intervals
        if (intervals <= 2) return

        const step = (scale.MaxYield - scale.MinYield) / (intervals - 2)
        const min = scale.MinYield

        const yieldValue = (index: number) => min + (Math.round((index * step) * 10) / 10)

        this.intervals = createRange(0, intervals, index => {

            const start = (!index) ? Number.MIN_VALUE : yieldValue(index - 1)
            const end = (index == (intervals - 1)) ? Number.MAX_VALUE : yieldValue(index)

            const color = scale.MapStyle[index]

            return {
                label: ((!index) ? `< ${min}` : String(start)) + " t/ha",
                min: start,
                max: end,
                color,
                cssColor: colorToCss(color)
            }

        })

    }

    private getCustomCropScale(): ICropYieldScale {

        const dl = this._layer.datalayer

        let cropId = dl.CropId;
        let year = dl.FirstYearId;

        if (dl.CroppingId) {
            const cropping = this._layer.context.loader.getCropping(dl.CroppingId);
            if (cropping) {
                cropId = cropping.cropId;
                year = cropping.cropping.YearId;
            }
        }

        var scales = this._layer.context.loader.getCropYieldScales(year);
        if (cropId && scales) {
            var cropScale = scales.filter(s => s.CropId === cropId);
            if (cropScale.length >= 1) {
                return cropScale[0];
            }
        }

        return null;
    }

}
